// apiClient.js - Updated for dual auth support (OAuth session + API key fallback)
import { logger } from "../utils/logger";

export const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3100";

// Check if OAuth is enabled (default: true)
// Set REACT_APP_USE_OAUTH=false in .env to use legacy API key auth
const useOAuth = process.env.REACT_APP_USE_OAUTH !== 'false';

// Legacy API key (only used when OAuth disabled)
const BACKEND_API_KEY = process.env.REACT_APP_BACKEND_API_KEY;

// Validate API key only when OAuth is disabled
if (!useOAuth && !BACKEND_API_KEY) {
  throw new Error(
    "Missing API Key! Set REACT_APP_BACKEND_API_KEY or enable OAuth (REACT_APP_USE_OAUTH=true)."
  );
}

if (!process.env.REACT_APP_API_BASE_URL) {
  logger.warn(
    "WARNING: REACT_APP_API_BASE_URL is not set. Using default: http://localhost:3100"
  );
}

/**
 * Get headers for API requests
 * When OAuth is enabled: Only Content-Type (session cookie handles auth)
 * When OAuth is disabled: Include Authorization header with API key
 */
const getHeaders = (contentType = "application/json") => {
  const headers = {
    "Content-Type": contentType,
  };

  // Only add API key header when OAuth is disabled
  if (!useOAuth && BACKEND_API_KEY) {
    headers["Authorization"] = `ApiKey ${BACKEND_API_KEY}`;
  }

  return headers;
};

/**
 * A central function to call any API endpoint using different HTTP methods.
 * Supports dual auth: session cookies (OAuth) or API key (legacy)
 * Now supports AbortController for request cancellation
 *
 * @param {string} endpoint The specific path after the BASE_URL (e.g., /api/projects).
 * @param {string} method The HTTP method (GET, POST, PUT, DELETE, PATCH). Default is GET.
 * @param {object | null} body The request body data for POST/PUT/PATCH.
 * @param {object} fetchOptions Additional options (e.g., { signal: abortController.signal })
 * @returns {Promise<Response>} The raw Fetch API Response object.
 */
export const apiRequest = async (endpoint, method = "GET", body = null, fetchOptions = {}) => {
  if (typeof endpoint !== "string") {
    throw new Error(`Invalid endpoint: Must be a string.`);
  }

  const url = `${BASE_URL}${endpoint}`;

  const options = {
    method: method.toUpperCase(),
    headers: getHeaders(),
    ...fetchOptions, // Spread additional options (including signal)
  };

  // Add credentials for session auth when OAuth is enabled
  // This sends the session cookie (tqp.sid) with requests
  if (useOAuth) {
    options.credentials = 'include';
  }

  // Only add a 'body' property if a body is provided AND the method is not 'GET' or 'HEAD'
  if (body && options.method !== "GET" && options.method !== "HEAD") {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);

    // Handle 401 Unauthorized - redirect to login (only when OAuth enabled)
    if (response.status === 401 && useOAuth) {
      // Don't redirect if already on login or auth callback pages
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/login') && !currentPath.startsWith('/auth')) {
        window.location.href = '/login';
      }
      throw new Error('Session expired. Please log in again.');
    }

    // Check for bad HTTP status (4xx, 5xx)
    if (!response.ok) {
      const errorText = await response.text();
      let errorData = null;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      throw new APIError(response.status, response.statusText, errorData);
    }

    // Return the successful Response object
    // The calling function (e.g., in the component/service) will call .json() or .text()
    return response;
  } catch (error) {
    // Don't log AbortErrors - they're expected when cancelling requests
    if (error.name !== 'AbortError') {
      logger.error(`Error in apiRequest for ${url}:`, error);
    }
    throw error;
  }
};

// --- API Error Class ---

/**
 * Custom error class for API failures with structured error information
 */
export class APIError extends Error {
  constructor(status, statusText, data) {
    super(statusText);
    this.name = 'APIError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.userMessage = this.getUserMessage();
  }

  getUserMessage() {
    // Extract backend message if available
    if (this.data?.message) {
      return Array.isArray(this.data.message)
        ? this.data.message[0]
        : this.data.message;
    }
    // Fallback to status-based messages
    if (this.status === 401) return 'Session expired. Please log in again.';
    if (this.status === 403) {
      return 'You do not have permission to perform this action. Contact an administrator if you need access.';
    }
    if (this.status === 404) return 'The requested resource was not found.';
    if (this.status >= 500) return 'Server error. Please try again later.';
    return 'Request failed. Please try again.';
  }
}

// --- Upload Error Class ---

/**
 * Custom error class for upload failures with SharePoint error mapping
 */
export class UploadError extends Error {
  constructor(status, responseText) {
    const message = UploadError.getErrorMessage(status, responseText);
    super(message);
    this.name = 'UploadError';
    this.status = status;
    this.userMessage = message;
  }

  static getErrorMessage(status, responseText) {
    switch (status) {
      case 401:
        return 'Authentication required. Please sign in with Microsoft.';
      case 409:
        return 'A file with this name already exists. Please rename and try again.';
      case 413:
        return 'File is too large. Maximum size is 50MB.';
      case 507:
        return 'Storage quota exceeded. Please contact your administrator.';
      default:
        if (status >= 500) return 'Server error. Please try again later.';
        if (status >= 400) return 'Upload failed. Please check the file and try again.';
        return 'Upload failed. Please check your connection.';
    }
  }
}

// --- File Upload Function ---

/**
 * Upload a file using multipart/form-data with progress tracking
 * Uses XMLHttpRequest for progress events (fetch doesn't support upload progress)
 *
 * @param {string} endpoint - API endpoint (e.g., /api/test-cases/{id}/attachments)
 * @param {File} file - File object to upload
 * @param {function} onProgress - Optional progress callback receiving percentage (0-100)
 * @returns {Promise<{ok: boolean, status: number, json: function}>} Response-like object
 */
export const uploadFile = (endpoint, file, onProgress = null) => {
  const url = `${BASE_URL}${endpoint}`;
  const formData = new FormData();
  formData.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Progress tracking
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          let percent = Math.round((event.loaded / event.total) * 100);
          // Keep UI in sync: reserve 100% for when server responds (load event)
          if (percent === 100) percent = 99;
          onProgress(percent);
        }
      });
    }

    // Handle successful completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Finalize progress at 100% when server response is received
        if (onProgress) {
          try { onProgress(100); } catch {}
        }
        resolve({
          ok: true,
          status: xhr.status,
          json: () => {
            try {
              return Promise.resolve(JSON.parse(xhr.responseText));
            } catch {
              return Promise.resolve({});
            }
          },
        });
      } else {
        logger.error(`Upload failed: ${xhr.status} for ${url}`);
        reject(new UploadError(xhr.status, xhr.responseText));
      }
    });

    // Handle network errors
    xhr.addEventListener('error', () => {
      logger.error(`Upload network error for ${url}`);
      reject(new UploadError(0, 'Network error'));
    });

    // Handle abort
    xhr.addEventListener('abort', () => {
      logger.warn(`Upload aborted for ${url}`);
      reject(new UploadError(0, 'Upload cancelled'));
    });

    xhr.open('POST', url);

    // Auth handling (no Content-Type header - browser sets multipart boundary automatically)
    if (useOAuth) {
      xhr.withCredentials = true;
    } else if (BACKEND_API_KEY) {
      xhr.setRequestHeader('Authorization', `ApiKey ${BACKEND_API_KEY}`);
    }

    xhr.send(formData);
  });
};

// --- Convenience Functions (They still call apiRequest and return the raw Response) ---

export const get = (endpoint, fetchOptions = {}) => apiRequest(endpoint, "GET", null, fetchOptions);

export const post = (endpoint, body, fetchOptions = {}) => apiRequest(endpoint, "POST", body, fetchOptions);

export const put = (endpoint, body, fetchOptions = {}) => apiRequest(endpoint, "PUT", body, fetchOptions);

export const patch = (endpoint, body, fetchOptions = {}) => apiRequest(endpoint, "PATCH", body, fetchOptions);

export const del = (endpoint, fetchOptions = {}) => apiRequest(endpoint, "DELETE", null, fetchOptions);
